import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import selectClassApi, {
  ClassType,
  SelectClassValues,
} from 'core/application/adminDashboard/benefitDetails/design/productClass/selectClass'

import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Alert from 'common/components/feedback/Alert'

import { commandRequest } from 'common/RequestUtils'

import { BaseComponentProps } from '../../../../common/productClassWizardModal/Common'

import './ProductClassStyles.less'

const formValidator = (translate: any) =>
  Yup.object({
    classType: Yup.number().nullable().required(translate('general.required')),
  })

const SelectClass: React.FC<BaseComponentProps<SelectClassValues>> = (props) => {
  const { bindSubmitFormHandler } = props
  const [errors, setErrors] = useState<{
    hasError: boolean
    message: string | undefined
  }>({
    hasError: false,
    message: undefined,
  })
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams<any>()

  useEffect(() => {
    props.setIsFormValid(formValidator(translate).isValidSync(props.state))
  }, [])

  const onSubmit = async (values: SelectClassValues) => {
    values.productClassId = props.state.productClassId
    values.planPeriodId = planPeriod
    const response = await commandRequest(() => selectClassApi(values))
    if (response.errors) {
      props.setIsSubmiting(false)
      setErrors({
        hasError: true,
        message: response.errors[0],
      })
    } else {
      if (!props.state.productClassId) {
        props.setProductClassId(response)
      }
      if (props.editing) {
        props.save()
      } else {
        props.goNext()
      }
    }
  }

  const errorMessageContent = (): ReactNode => {
    if (errors.hasError) {
      return <Alert type="error" message={errors.message} />
    }
    return null
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={props.state}
      onSubmit={onSubmit}
      validate={(values) => {
        const isValid: boolean = formValidator(translate).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        setErrors({
          hasError: false,
          message: undefined,
        })
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form autoComplete="off" onSubmit={handleSubmit} className="select_class">
            <Row>
              <Col span={12}>
                <h5 className="select_class_info pl-8">
                  {translate('admin-dashboard.benefit-details.class-design.select-class.header')}
                </h5>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Radio.Group name="classType" defaultValue={props.state.classType}>
                  <Row gutter={10}>
                    <Col className="mb-8" span={12}>
                      <Radio.Button
                        className="large radio-with-checkmark select_class__radio"
                        value={ClassType.FullTime}
                      >
                        <div className="pl-16 pr-24 content">
                          {translate('admin-dashboard.benefit-details.class-design.select-class.full-time')}
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col className="mb-8" span={12}>
                      <Radio.Button
                        className="large radio-with-checkmark select_class__radio"
                        value={ClassType.PartTime}
                      >
                        <div className="pl-16 pr-24 content">
                          {translate('admin-dashboard.benefit-details.class-design.select-class.part-time')}
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col className="mb-8" span={12}>
                      <Radio.Button
                        size="large"
                        className="large radio-with-checkmark select_class__radio"
                        value={ClassType.Salaried}
                      >
                        <div className="pl-16 pr-24 content">
                          {translate('admin-dashboard.benefit-details.class-design.select-class.salaried')}
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col className="mb-8" span={12}>
                      <Radio.Button
                        size="large"
                        className="large radio-with-checkmark select_class__radio"
                        value={ClassType.Hourly}
                      >
                        <div className="pl-16 pr-24 content">
                          {translate('admin-dashboard.benefit-details.class-design.select-class.hourly')}
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col className="mb-8" span={12}>
                      <Radio.Button
                        className="large radio-with-checkmark select_class__radio"
                        value={ClassType.Manager}
                      >
                        <div className="pl-16 pr-24 content">
                          {translate('admin-dashboard.benefit-details.class-design.select-class.manager')}
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col className="mb-8" span={12}>
                      <Radio.Button className="large radio-with-checkmark select_class__radio" value={ClassType.Staff}>
                        <div className="pl-16 pr-24 content">
                          {translate('admin-dashboard.benefit-details.class-design.select-class.staff')}
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col className="mb-8" span={12}>
                      <Radio.Button
                        className="large radio-with-checkmark select_class__radio"
                        value={ClassType.Executive}
                      >
                        <div className="pl-16 pr-24 content">
                          {translate('admin-dashboard.benefit-details.class-design.select-class.executive')}
                        </div>
                      </Radio.Button>
                    </Col>
                  </Row>
                </Radio.Group>
              </Col>
            </Row>
            <Row className="alert-message">
              <Col>{errorMessageContent()}</Col>
            </Row>
          </form>
        )
      }}
    </Formik>
  )
}

export default SelectClass
